// Web Audio API-based sound synthesis for DareGames
// Zero external dependencies — all sounds generated in-browser.

class SoundManager {
  constructor() {
    this.enabled = true;
    this.ctx = null;
    this.masterGain = null;
    this.bgm = null;
    this.rain = null;
  }

  _ctx() {
    if (!this.ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      this.ctx = new AC();
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = 0.5;
      this.masterGain.connect(this.ctx.destination);
    }
    if (this.ctx.state === "suspended") {
      this.ctx.resume().catch(() => {});
    }
    return this.ctx;
  }

  // ---- Public one-shots ----
  play(key) {
    if (!this.enabled) return;
    const ctx = this._ctx();
    if (!ctx) return;
    try {
      if (key === "click") this._click();
      else if (key === "milestone") this._milestone();
      else if (key === "crash") this._crash();
      else if (key === "cheer") this._cheer();
    } catch {}
  }

  // Backwards-compat aliases used by older UI code
  playLoop(key) {
    if (key === "bgm") this.playBGM();
    else if (key === "thunder") this.playRain();
  }
  stop(key) {
    if (key === "bgm") this.stopBGM();
    else if (key === "thunder") this.stopRain();
  }

  stopAll() {
    this.stopBGM();
    this.stopRain();
  }

  // ---- Click: short bright square blip ----
  _click() {
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "square";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1400, ctx.currentTime + 0.04);
    g.gain.setValueAtTime(0.22, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);
    osc.connect(g).connect(this.masterGain);
    osc.start();
    osc.stop(ctx.currentTime + 0.1);
  }

  // ---- Milestone: upward 8-bit arpeggio ----
  _milestone() {
    const ctx = this.ctx;
    const notes = [523.25, 659.25, 783.99, 1046.5]; // C E G C
    notes.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "square";
      osc.frequency.value = f;
      const t = ctx.currentTime + i * 0.06;
      g.gain.setValueAtTime(0.001, t);
      g.gain.exponentialRampToValueAtTime(0.18, t + 0.005);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.14);
      osc.connect(g).connect(this.masterGain);
      osc.start(t);
      osc.stop(t + 0.16);
    });
  }

  // ---- Crash: descending sawtooth + noise burst ----
  _crash() {
    const ctx = this.ctx;
    // noise burst
    const bufSize = ctx.sampleRate * 0.5;
    const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
    const nd = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) {
      nd[i] = (Math.random() * 2 - 1) * (1 - i / bufSize);
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const nf = ctx.createBiquadFilter();
    nf.type = "lowpass";
    nf.frequency.setValueAtTime(2500, ctx.currentTime);
    nf.frequency.exponentialRampToValueAtTime(120, ctx.currentTime + 0.5);
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.55, ctx.currentTime);
    ng.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.55);
    src.connect(nf).connect(ng).connect(this.masterGain);
    src.start();

    // low descending saw
    const osc = ctx.createOscillator();
    const og = ctx.createGain();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(240, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(45, ctx.currentTime + 0.6);
    og.gain.setValueAtTime(0.28, ctx.currentTime);
    og.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.7);
    osc.connect(og).connect(this.masterGain);
    osc.start();
    osc.stop(ctx.currentTime + 0.7);
  }

  // ---- Cheer: filtered noise "roar" + triangle chord ----
  _cheer() {
    const ctx = this.ctx;
    const bufSize = ctx.sampleRate * 1.4;
    const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
    const nd = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) nd[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 1600;
    bp.Q.value = 1.2;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.001, ctx.currentTime);
    ng.gain.exponentialRampToValueAtTime(0.35, ctx.currentTime + 0.35);
    ng.gain.setValueAtTime(0.35, ctx.currentTime + 0.8);
    ng.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1.4);
    src.connect(bp).connect(ng).connect(this.masterGain);
    src.start();

    [523.25, 659.25, 783.99].forEach((f, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.value = f;
      const t = ctx.currentTime + i * 0.08;
      g.gain.setValueAtTime(0.001, t);
      g.gain.exponentialRampToValueAtTime(0.16, t + 0.1);
      g.gain.exponentialRampToValueAtTime(0.001, t + 1.0);
      osc.connect(g).connect(this.masterGain);
      osc.start(t);
      osc.stop(t + 1.1);
    });
  }

  // ---- BGM: rhythmic bass pulse, tempo scales with score ----
  playBGM() {
    if (!this.enabled) return;
    const ctx = this._ctx();
    if (!ctx) return;
    if (this.bgm) return; // already running
    const pattern = [55, 55, 82.4, 55, 65.4, 65.4, 98, 65.4]; // A1 style groove
    const state = {
      running: true,
      speedMultiplier: 1.0,
      stepIndex: 0,
      nextTime: ctx.currentTime + 0.05,
      timer: null,
    };
    const scheduler = () => {
      if (!state.running) return;
      // schedule up to 300ms ahead
      while (state.nextTime < ctx.currentTime + 0.3) {
        this._bgmStep(state.nextTime, pattern[state.stepIndex % pattern.length]);
        state.stepIndex += 1;
        const stepDur = 0.2 / state.speedMultiplier;
        state.nextTime += stepDur;
      }
      state.timer = setTimeout(scheduler, 40);
    };
    scheduler();
    this.bgm = state;
  }

  _bgmStep(t, freq) {
    const ctx = this.ctx;
    // bass note
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "sawtooth";
    osc.frequency.value = freq;
    g.gain.setValueAtTime(0.001, t);
    g.gain.exponentialRampToValueAtTime(0.13, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
    osc.connect(g).connect(this.masterGain);
    osc.start(t);
    osc.stop(t + 0.22);

    // occasional hi-hat noise
    if (Math.random() < 0.4) {
      const nb = ctx.createBuffer(1, ctx.sampleRate * 0.04, ctx.sampleRate);
      const nd = nb.getChannelData(0);
      for (let i = 0; i < nd.length; i++) nd[i] = Math.random() * 2 - 1;
      const ns = ctx.createBufferSource();
      ns.buffer = nb;
      const hp = ctx.createBiquadFilter();
      hp.type = "highpass";
      hp.frequency.value = 6500;
      const ng = ctx.createGain();
      ng.gain.setValueAtTime(0.06, t);
      ng.gain.exponentialRampToValueAtTime(0.001, t + 0.04);
      ns.connect(hp).connect(ng).connect(this.masterGain);
      ns.start(t);
    }
  }

  stopBGM() {
    if (!this.bgm) return;
    this.bgm.running = false;
    if (this.bgm.timer) clearTimeout(this.bgm.timer);
    this.bgm = null;
  }

  setBgmSpeed(rate) {
    if (this.bgm) this.bgm.speedMultiplier = Math.max(0.6, Math.min(2.2, rate));
  }

  // ---- Rain + thunder ----
  // Accepts a durationMs; the rain self-stops after that window. Any
  // concurrent playRain() call cancels the previous rain + its scheduled
  // stop-timer, so overlapping rain events never leak audio past their
  // visual window.
  playRain(durationMs = 7000) {
    if (!this.enabled) return;
    const ctx = this._ctx();
    if (!ctx) return;
    this.stopRain();

    // Sustained filtered noise for rain
    const bufSize = ctx.sampleRate * 2;
    const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
    const nd = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) nd[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 900;
    const g = ctx.createGain();
    g.gain.value = 0.14;
    src.connect(lp).connect(g).connect(this.masterGain);
    src.start();

    const state = { src, g, thunderTimer: null, autoStopTimer: null, running: true };
    // schedule thunder spikes
    const boom = () => {
      if (!state.running) return;
      const t = ctx.currentTime + 0.05;
      const tb = ctx.createBuffer(1, ctx.sampleRate * 0.6, ctx.sampleRate);
      const td = tb.getChannelData(0);
      for (let i = 0; i < td.length; i++)
        td[i] = (Math.random() * 2 - 1) * (1 - i / td.length);
      const tsrc = ctx.createBufferSource();
      tsrc.buffer = tb;
      const tf = ctx.createBiquadFilter();
      tf.type = "lowpass";
      tf.frequency.setValueAtTime(400, t);
      tf.frequency.exponentialRampToValueAtTime(80, t + 0.6);
      const tg = ctx.createGain();
      tg.gain.setValueAtTime(0.7, t);
      tg.gain.exponentialRampToValueAtTime(0.001, t + 0.6);
      tsrc.connect(tf).connect(tg).connect(this.masterGain);
      tsrc.start(t);
      state.thunderTimer = setTimeout(boom, 1500 + Math.random() * 2200);
    };
    state.thunderTimer = setTimeout(boom, 400);
    // Self-stop once the visual rain window is over — no external timer
    // needed from the game page.
    state.autoStopTimer = setTimeout(() => this.stopRain(), durationMs);
    this.rain = state;
  }

  stopRain() {
    if (!this.rain) return;
    this.rain.running = false;
    if (this.rain.thunderTimer) clearTimeout(this.rain.thunderTimer);
    if (this.rain.autoStopTimer) clearTimeout(this.rain.autoStopTimer);
    try {
      this.rain.src.stop();
    } catch {}
    this.rain = null;
  }
}

export const sounds = new SoundManager();
